import { useRouter } from 'next/router';

import { Center, Spinner } from '@vimeo/bokeh';
import { ShowcaseBanner } from '@vimeo/viewer-showcase-ui/components/ShowcaseBanner';

import { useGetFeaturedVideo } from '../hooks/use-get-featured-video';
import { useGetFirstPlaylistVideo } from '../hooks/use-get-first-playlist-video';
import { useGetHeaderLayoutConfig } from '../hooks/use-get-header-layout-config';
import { useGetShowcase } from '../hooks/use-get-showcase';
import { useLiveShowcaseData } from '../hooks/use-live-showcase-data';
import { useShowNewCustomizeControls } from '../hooks/use-show-new-customize-controls';
import { useAlbumStore } from '../state/albumStore';

export const BannerModule = ({
  isShowcaseEmpty,
  showVideoDetails,
  bannerHeight = '50vh',
}: {
  isShowcaseEmpty: boolean;
  showVideoDetails: boolean;
  bannerHeight?: string;
}) => {
  const isNewCustomizeControls = useShowNewCustomizeControls();
  const router = useRouter();
  const { featuredVideo } = useGetFeaturedVideo();

  const {
    thumbnailUrl,
    showBannerDescription,
    showBannerTitle,
    showBannerHeader,
    showStartWatchingButton,
    textCtaAlignment,
  } = useGetShowcase();
  const showcaseId = useAlbumStore((state) => state.albumId);
  const layoutConfig = useGetHeaderLayoutConfig({ isShowcaseEmpty, showVideoDetails });
  const { startTime, archivedTime, isStreaming, isUpcoming } = useLiveShowcaseData(
    featuredVideo?.clipId,
  );

  const { firstClipLink } = useGetFirstPlaylistVideo(!featuredVideo); // We need the link of the first video for the watch button

  const onWatchClick = () => {
    //  if there is no featured video, play first video in queue
    const watchLink = featuredVideo?.link || firstClipLink;
    if (!watchLink) return;
    router.push(watchLink);
  };

  // Show loading state while featured/live video is loading
  if (!layoutConfig) {
    const height =
      isShowcaseEmpty && !thumbnailUrl
        ? { base: '220px', sm: '294px' }
        : { base: '352px', sm: '480px' };
    return (
      <Center minH={height} height={isShowcaseEmpty && !thumbnailUrl ? height : bannerHeight}>
        <Spinner size="md" color="text-primary" />
      </Center>
    );
  }

  return (
    <ShowcaseBanner
      isNewCustomizeControls={isNewCustomizeControls}
      onWatchClick={onWatchClick}
      mainTitle={showBannerTitle ? layoutConfig.mainTitle : ''}
      topTitle={layoutConfig.topTitle}
      description={showBannerDescription ? layoutConfig.description : ''}
      thumbnailUrl={layoutConfig.thumbnailUrl}
      isLive={layoutConfig.isLive}
      startTime={startTime}
      archivedTime={archivedTime}
      isStreaming={isStreaming}
      isUpcoming={isUpcoming}
      isShowcaseEmpty={isShowcaseEmpty}
      showVideoDetails={showVideoDetails}
      bannerHeight={bannerHeight}
      showLinksInDescription={layoutConfig.showLinksInDescription}
      showBannerDescription={showBannerDescription}
      showBannerTitle={showBannerTitle}
      showBannerHeader={showBannerHeader}
      showcaseId={showcaseId}
      showStartWatchingButton={showStartWatchingButton}
      textCtaAlignment={textCtaAlignment}
    />
  );
};
